import classNames from 'classnames';
import { Link } from 'gatsby';
import React, { useState, useEffect } from 'react';
import Logo from 'svg/logo-with-grafana-labs.inline.svg';
import { blog, docs } from 'utils/urls';

import styles from './footer.module.scss';
import AdoptBadge from './svg/adopt.inline.svg';
import G2Banner from './svg/g2.inline.svg';
import GithubLogo from './svg/github.inline.svg';
import HeartIcon from './svg/heart-purple.inline.svg';
import LinkedinLogo from './svg/linkedin.inline.svg';
import MeetupLogo from './svg/meetup.inline.svg';
import SlackLogo from './svg/slack.inline.svg';
import TwitterLogo from './svg/twitter.inline.svg';
import YoutubeLogo from './svg/youtube.inline.svg';

const Badge = ({ children }) => <span className={styles.badge}>{children}</span>;

const Heart = () => (
  <span className={styles.heart} role="img" aria-label="heart">
    <HeartIcon />
  </span>
);

const fetchStatusIndicator = async (callback) => {
  try {
    const result = await fetch('https://status.k6.io/api/v2/status.json').then((res) => res.json());
    callback(result?.status?.indicator || 'none');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('failed while fetching status', e);
  }
};

export const Footer = () => {
  // possible values: 'none', 'minor', 'major', 'critical'
  const [jobs, setJobs] = useState(null);

  useEffect(() => {
    fetch(`${process.env.GATSBY_DEFAULT_MAIN_URL}/data/jobs-positions.json`)
      .then((response) => response.json())
      .then((data) => setJobs(data))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch jobs: ', error);
      });
  }, []);

  return (
    <footer className={classNames(styles.wrapper)}>
      <div className={`container ${styles.relative}`}>
        <div className={styles.topSection}>
          <div className={'row'}>
            <div className={`${classNames('col-2 col-xl-2 col-lg-12 col-md-12 col-sm-12', styles.logoWrapper)}`}>
              <Link className={styles.logoLink} to={'/'}>
                <Logo className={styles.logo} />
              </Link>
            </div>

            <div className={'col-9 col-xl-9 col-lg-12 col-md-12 col-sm-12'}>
              <div className={'row'}>
                <div className={'col-xl-3 col-md-6 col-sm-12'}>
                  <h3 className={styles.navColumnTitle}>Product</h3>
                  <ul className={styles.navColumnList}>
                    <li className={styles.navColumnItem}>
                      <Link className={styles.navColumnLink} to={'/open-source/'}>
                        Open Source
                      </Link>
                    </li>
                    <li className={styles.navColumnItem}>
                      <a className={styles.navColumnLink} href={'https://grafana.com/products/cloud/k6/?src=k6io'}>
                        Grafana Cloud k6
                      </a>
                    </li>
                    <li className={styles.navColumnItem}>
                      <a className={styles.navColumnLink} href={'https://grafana.com/pricing/?src=k6io'}>
                        Grafana Cloud k6 Pricing
                      </a>
                    </li>
                    <li className={styles.navColumnItem}>
                      <a className={styles.navColumnLink} href={'/oss-vs-cloud/'}>
                        Open Source vs Cloud
                      </a>
                    </li>
                    <li className={styles.navColumnItem}>
                      <Link className={styles.navColumnLink} to={'/testimonials/'}>
                        Testimonials
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className={'col-xl-3 col-md-6 col-sm-12'}>
                  <h3 className={styles.navColumnTitle}>Resources</h3>
                  <ul className={styles.navColumnList}>
                    <li className={styles.navColumnItem}>
                      <a className={styles.navColumnLink} href={`${docs}`}>
                        k6 Docs
                      </a>
                    </li>
                    <li className={styles.navColumnItem}>
                      <a className={styles.navColumnLink} href={'https://grafana.com/docs/grafana-cloud/k6'}>
                        Grafana Cloud k6 Docs
                      </a>
                    </li>
                    <li className={styles.navColumnItem}>
                      <a className={styles.navColumnLink} href={'https://grafana.com/docs/k6/latest/extensions/'}>
                        Extensions
                      </a>
                    </li>
                    <li className={styles.navColumnItem}>
                      <a className={styles.navColumnLink} href={'https://grafana.com/docs/k6/latest/integrations/'}>
                        Integrations
                      </a>
                    </li>
                  </ul>
                </div>
                <div className={'col-xl-3 col-md-6 col-sm-12'}>
                  <h3 className={styles.navColumnTitle}>Community</h3>
                  <ul className={styles.navColumnList}>
                    <li className={styles.navColumnItem}>
                      <a className={styles.navColumnLink} href={'https://grafana.com/community/'}>
                        Engage <Heart />
                      </a>
                    </li>
                    <li className={styles.navColumnItem}>
                      <a className={styles.navColumnLink} href={'https://community.grafana.com'}>
                        Forum
                      </a>
                    </li>
                    <li className={styles.navColumnItem}>
                      <a className={styles.navColumnLink} href={'https://github.com/grafana?q=k6'}>
                        GitHub
                      </a>
                    </li>
                  </ul>
                </div>
                <div className={`${classNames('col-xl-3 col-md-6 col-sm-12 ')}`}>
                  <h3 className={styles.navColumnTitle}>About</h3>
                  <ul className={styles.navColumnList}>
                    <li className={styles.navColumnItem}>
                      <a className={styles.navColumnLink} href={`${blog}`}>
                        Blog
                      </a>
                    </li>
                    <li className={styles.navColumnItem}>
                      <Link className={styles.navColumnLink} to={'/about/'}>
                        Our story
                      </Link>
                    </li>
                    <li className={styles.navColumnItem}>
                      <Link className={styles.navColumnLink} to={'/our-beliefs/'}>
                        Our beliefs
                      </Link>
                    </li>
                    <li className={styles.navColumnItem}>
                      <a className={styles.navColumnLink} href={'https://grafana.com/contact'}>
                        Contact
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className={`${classNames('col-xl-1 col-lg-12 col-md-12 col-sm-12', styles.bannerWrapper)}`}>
              <a
                className={styles.g2Banner}
                href="https://www.g2.com/products/k6/reviews"
                target="_blank"
                rel="noreferrer"
              >
                <G2Banner className={styles.g2Icon} />
              </a>
              <a
                className={styles.adoptBadge}
                href="https://www.thoughtworks.com/radar/tools/k6"
                target="_blank"
                rel="noreferrer"
              >
                <AdoptBadge className={styles.adoptIcon} />
              </a>
            </div>
          </div>
        </div>

        <div className={'row align-items-end'}>
          <div className={'col-xl-10 col-lg-9 col-md-9 col-auto'}>
            <ul className={styles.legalLinksList}>
              <li className={styles.navColumnItem}>
                <a className={styles.navColumnLink} href={'https://grafana.com/legal/?src=k6io'}>
                  Legal and Security
                </a>
              </li>
              <li className={styles.navColumnItem}>
                <a className={styles.navColumnLink} href={'https://grafana.com/legal/privacy-policy/?src=k6io'}>
                  Privacy Policy
                </a>
              </li>
            </ul>
          </div>
          <div className={'col-xl-2 col-lg-3 col-md-3'}>
            <p className={`${styles.text}`}>© 2024 Grafana Labs</p>
          </div>
        </div>
      </div>
    </footer>
  );
};
